import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { getAuth, RecaptchaVerifier, signInWithPhoneNumber } from 'firebase/auth';
import { useTranslation } from 'react-i18next';
import { Container, Row, Col, Form, Button, Card, Alert } from 'react-bootstrap';
import PhoneInput, { CountrySelectArrow } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { FaCaretDown, FaInfoCircle } from 'react-icons/fa';
import './Auth.css';

const PhoneLogin = () => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: Phone input, 2: Verification code
  const navigate = useNavigate();
  const { t } = useTranslation();
  const auth = getAuth();
  
  // Clean up any existing reCAPTCHA widgets when component mounts/unmounts
  useEffect(() => {
    // Clean up function to run on unmount
    return () => {
      if (window.recaptchaVerifier) {
        try {
          window.recaptchaVerifier.clear();
          window.recaptchaVerifier = null;
        } catch (e) {
          console.error('Error clearing reCAPTCHA:', e);
        }
      }
    };
  }, []);
  
  // Create a separate, detached div for reCAPTCHA to avoid React rerendering issues
  const setupRecaptcha = () => {
    if (window.recaptchaVerifier) {
      try {
        window.recaptchaVerifier.clear();
      } catch (e) {
        console.error('Error clearing existing reCAPTCHA:', e);
      }
      window.recaptchaVerifier = null;
    }
    
    // Create a separate container outside React's control
    let recaptchaContainer = document.createElement('div');
    recaptchaContainer.id = 'recaptcha-container-' + Date.now();
    document.body.appendChild(recaptchaContainer);
    
    try {
      // Try normal workflow first
      window.recaptchaVerifier = new RecaptchaVerifier(auth, recaptchaContainer.id, {
        'size': 'normal', // Use normal size for better reliability
        'callback': () => {
          console.log('reCAPTCHA solved!');
          // Hide the container after solving
          recaptchaContainer.classList.remove('active');
        },
        'expired-callback': () => {
          setError('reCAPTCHA expired. Please try again.');
        }
      });
      
      // Show the container when reCAPTCHA is about to be displayed
      recaptchaContainer.classList.add('active');
    } catch (err) {
      console.error('RecaptchaVerifier init error:', err);
      setError('There was a problem setting up verification. Please try again.');
    }
  };
  
  // Development mode check
  const isDev = process.env.NODE_ENV === 'development';
  
  const handleSendOTP = async (e) => {
    e.preventDefault();
    
    if (!phoneNumber) {
      return setError(t('phoneSignUp.errorInvalidNumber'));
    }
    
    setError('');
    setLoading(true);
    
    // DEV MODE WORKAROUND - Skip actual Firebase for dev
    if (isDev) {
      console.log('[DEV MODE] Simulating phone auth for:', phoneNumber);
      
      // Create a fake confirmation object
      const mockConfirmation = {
        confirm: (code) => {
          // Check if the code is '123456' for testing
          if (code === '123456') {
            return Promise.resolve({
              user: {
                phoneNumber: phoneNumber,
                uid: 'dev-user-123',
              }
            });
          } else {
            return Promise.reject(new Error('Invalid code. Use 123456 for development.'));
          }
        }
      };
      
      // Set the mock confirmation object
      setConfirmationResult(mockConfirmation);
      
      // Move to the OTP step
      setStep(2);
      setLoading(false);
      
      // Show a developer info
      console.info('[DEV MODE] Use verification code 123456 for testing');
      return;
    }
    
    // PRODUCTION MODE - Use actual Firebase phone auth
    try {
      // Set up reCAPTCHA first
      setupRecaptcha();
      
      // Make sure the reCAPTCHA verifier is rendered before proceeding
      if (!window.recaptchaVerifier) {
        throw new Error('reCAPTCHA setup failed');
      }
      
      // This will render the reCAPTCHA widget
      await window.recaptchaVerifier.render();
      
      // Now that the reCAPTCHA is rendered, we can proceed with phone auth
      const confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, window.recaptchaVerifier);
      setConfirmationResult(confirmationResult);
      setStep(2);
      
      // Clean up the reCAPTCHA container after successful verification
      try {
        // Find and remove all reCAPTCHA containers
        const containers = document.querySelectorAll('div[id^="recaptcha-container-"]');
        containers.forEach(container => {
          if (container && container.parentNode) {
            container.parentNode.removeChild(container);
          }
        });
      } catch (cleanupError) {
        console.error('Error cleaning up reCAPTCHA:', cleanupError);
      }
    } catch (err) {
      console.error('Phone auth error:', err);
      
      if (err.code === 'auth/invalid-phone-number') {
        setError(t('phoneSignUp.errorInvalidNumber'));
      } else if (err.code === 'auth/too-many-requests') {
        setError('Too many login attempts. Please try again later.');
      } else if (err.code === 'auth/captcha-check-failed') {
        setError('reCAPTCHA verification failed. Please try again.');
      } else if (err.code === 'auth/invalid-app-credential') {
        setError('Firebase phone auth is not properly configured. Please contact the administrator.');
      } else {
        setError(err.message || 'An error occurred during phone verification.');
      }
      
      // Reset reCAPTCHA
      if (window.recaptchaVerifier) {
        try {
          window.recaptchaVerifier.clear();
        } catch (e) {
          console.error('Error clearing reCAPTCHA:', e);
        }
        window.recaptchaVerifier = null;
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    
    if (!verificationCode) {
      return setError(t('phoneSignUp.errorMissingOtp'));
    }
    
    setError('');
    setLoading(true);
    
    try {
      await confirmationResult.confirm(verificationCode);
      
      // Success message
      console.log('Successfully verified phone number');
      
      // If we're in development mode, inform the user
      if (isDev && verificationCode === '123456') {
        console.log('[DEV MODE] Successfully logged in with development code');
      }
      
      // Navigate home
      navigate('/');
    } catch (err) {
      console.error('OTP verification error:', err);
      
      if (isDev) {
        setError(`Use code "123456" for development. Error: ${err.message}`);
      } else {
        setError(err.message || 'Invalid verification code. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleBack = () => {
    setStep(1);
    setError('');
  };
  
  const handleGoToLogin = () => {
    navigate('/login');
  };

  return (
    <div className="auth-container">
      <Card className="auth-card">
        <Card.Body>
          <div className="auth-header">
            <h1 className="auth-title">{t('phoneSignUp.title')}</h1>
            <p className="auth-subtitle">
              {step === 1 ? t('phoneSignUp.phoneNumberLabel') : t('phoneSignUp.otpLabel')}
            </p>
          </div>
          
          {error && <Alert variant="danger" className="auth-error">{error}</Alert>}
          
          {step === 1 ? (
            <div className="auth-methods">
              <Form onSubmit={handleSendOTP}>
                <Form.Group id="phone" className="mb-4">
                  <Form.Label>{t('phoneSignUp.phoneNumberLabel')}</Form.Label>
                  <div className="custom-phone-input-wrapper">
                    <PhoneInput
                      international
                      defaultCountry="NO"
                      value={phoneNumber}
                      onChange={setPhoneNumber}
                      placeholder={t('phoneSignUp.phonePlaceholder')}
                      className="phone-input-container"
                      required
                    />
                    <div className="dropdown-icon-wrapper">
                      <FaCaretDown className="country-dropdown-arrow" size={16} />
                    </div>
                  </div>
                </Form.Group>
                
                {/* We're using a dynamic container so this is just for styling */}
                <div className="recaptcha-info-text">
                  {isDev 
                    ? (
                        <div className="dev-mode-notice">
                          <strong>Development Mode:</strong> Use verification code <code>123456</code> for testing
                        </div>
                      )
                    : t('phoneSignUp.recaptchaInfo')
                  }
                </div>
                
                <div className="d-grid gap-3 mt-4">
                  <Button 
                    type="submit" 
                    disabled={loading}
                    className="phone-btn"
                  >
                    {loading ? t('loading') : t('phoneSignUp.buttonSendOtp')}
                  </Button>
                </div>
              </Form>
              
              <div className="auth-footer">
                <Button 
                  variant="outline-secondary" 
                  className="cancel-btn"
                  onClick={handleGoToLogin}
                  disabled={loading}
                >
                  {t('phoneSignUp.buttonBack')}
                </Button>
              </div>
            </div>
          ) : (
            <div className="auth-methods">
              <Form onSubmit={handleVerifyOTP}>
                <Form.Group id="otp" className="mb-4">
                  <Form.Label>{t('phoneSignUp.otpLabel')}</Form.Label>
                  {isDev && (
                    <div className="dev-mode-hint mb-2">
                      <small className="text-muted">
                        <FaInfoCircle className="me-1" />
                        Development mode: Enter <strong>123456</strong>
                      </small>
                    </div>
                  )}
                  <Form.Control
                    type="text"
                    value={verificationCode}
                    onChange={(e) => {
                      // Only allow numbers and limit to 6 digits
                      const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 6);
                      setVerificationCode(value);
                    }}
                    placeholder={isDev ? "123456" : "000000"}
                    className="otp-input"
                    maxLength="6"
                    autoComplete="one-time-code"
                    required
                  />
                  <Form.Text className="text-muted">
                    {isDev 
                      ? "Development mode - any 6-digit code works, but use 123456 for testing" 
                      : `Enter the 6-digit code sent to ${phoneNumber}`
                    }
                  </Form.Text>
                </Form.Group>
                
                <div className="d-grid gap-3 mt-4">
                  <Button 
                    type="submit" 
                    disabled={loading}
                    className="phone-btn"
                  >
                    {loading ? t('loading') : t('phoneSignUp.buttonVerifyOtp')}
                  </Button>
                </div>
              </Form>
              
              <div className="auth-footer">
                <Button 
                  variant="outline-secondary" 
                  className="cancel-btn"
                  onClick={handleBack}
                  disabled={loading}
                >
                  {t('phoneSignUp.buttonBack')}
                </Button>
                
                <div className="terms-policy-notice">
                  <p className="terms-text">
                    <FaInfoCircle className="terms-icon" />
                    {t('loginTermsNotice')}
                    <Link 
                      to="/terms-policy" 
                      className="terms-link"
                      onClick={() => sessionStorage.setItem('returnToAfterTerms', '/phone-login')}
                    >
                      {t('termsAndPolicy')}
                    </Link>
                  </p>
                </div>
              </div>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default PhoneLogin;